import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Network, Compass, BookOpen, Building2, HelpCircle, FileText, Heart, Scale, IndianRupee, TrendingUp, Zap, Play, Calendar, Menu, X, Globe, Users, Eye, EyeOff, LogIn, User as UserIcon } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { FEATURES } from '../lib/featureFlags';
import AdminNotificationBell from './admin/AdminNotificationBell';

export default function Navbar() {
    const location = useLocation();
    const { savedColleges, compareList, userData, realUser, simulatedRole, setViewAsRole, clearViewAsRole } = useUser();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const isPhase1 = true; // Phase 1 (June 2026) — Design vertical only

    const isAdmin = realUser?.role === 'admin';
    // Phase 1 launch — only "user" and "admin" roles exist on the public site.
    // Investor/recruiter/college/partner dashboards are coming in Phase 2.
    const ROLES = ['user', 'admin'];

    const isActive = (path) => {
        return location.pathname.startsWith(path) && path !== '/' ? 'text-indigo-400 bg-indigo-500/10' :
            (location.pathname === path ? 'text-indigo-400 bg-indigo-500/10' : 'text-slate-300 hover:text-white hover:bg-slate-800 transition-colors');
    };

    const MobileLink = ({ to, icon: Icon, children }) => (
        <Link
            to={to}
            onClick={() => {
                setIsMobileMenuOpen(false);
                window.scrollTo(0, 0);
            }}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive(to)}`}
        >
            <Icon className="w-5 h-5" />
            <span className="font-medium">{children}</span>
        </Link>
    );

    const isViewAsActive = Boolean(simulatedRole) && realUser?.role === 'admin';

    return (
        <nav className={`fixed ${isViewAsActive ? 'top-[36px]' : 'top-0'} w-full bg-slate-900/90 backdrop-blur-md border-b border-slate-800 z-50 transition-all duration-300`}>
            <div className="w-full px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 z-50">
                        <span className="font-black text-2xl bg-gradient-to-r from-[#7dd3fc] via-[#bae6fd] to-white bg-clip-text text-transparent tracking-tighter hover:scale-105 transition-transform duration-300">
                            Eager
                        </span>
                    </Link>

                    {/* ——————————————————————————————————————————————————————————————————
                       DESKTOP NAVIGATION (Hidden on Mobile)
                       —————————————————————————————————————————————————————————————————— */}
                    <div className="hidden xl:flex gap-2 items-center">
                        <Link to="/explore" onClick={() => window.scrollTo(0, 0)} className={`px-2 py-2 rounded-md transition-all duration-200 flex items-center gap-1.5 text-sm font-bold ${isActive('/explore')}`}>
                            <Compass className="w-3.5 h-3.5" />
                            {isPhase1 ? 'Design Courses' : 'Courses'}
                        </Link>
                        <Link to="/library" onClick={() => window.scrollTo(0, 0)} className={`px-2 py-2 rounded-md transition-all duration-200 flex items-center gap-1.5 text-sm font-bold ${isActive('/library')}`}>
                            <Play className="w-3.5 h-3.5" />
                            Career Insights
                        </Link>
                        <Link to="/colleges" onClick={() => window.scrollTo(0, 0)} className={`px-2 py-2 rounded-md transition-all duration-200 flex items-center gap-1.5 text-sm font-bold ${isActive('/colleges')}`}>
                            <Building2 className="w-3.5 h-3.5" />
                            {isPhase1 ? 'Design Colleges' : 'Best Colleges'}
                        </Link>
                        
                        {/* Study Abroad hidden for Phase 1 */}

                        <Link to="/benefits" onClick={() => window.scrollTo(0, 0)} className={`px-2 py-2 rounded-md transition-all duration-200 flex items-center gap-1.5 text-sm font-bold ${isActive('/benefits')}`}>
                            <Zap className="w-3.5 h-3.5 text-amber-400" />
                            Benefits
                        </Link>

                        <Link to="/eagerminds" onClick={() => window.scrollTo(0, 0)} className={`px-2 py-2 rounded-md transition-all duration-200 flex items-center gap-1.5 text-sm font-bold ${isActive('/eagerminds')}`}>
                            <Users className="w-3.5 h-3.5 text-indigo-400" />
                            Eager Minds
                        </Link>

                        <Link to="/counseling" onClick={() => window.scrollTo(0, 0)} className={`px-2 py-2 rounded-md transition-all duration-200 flex items-center gap-1.5 text-sm font-bold ${isActive('/counseling')}`}>
                            <BookOpen className="w-3.5 h-3.5" />
                            {isPhase1 ? 'Design Counseling' : 'Counseling'}
                        </Link>

                        <div className="w-px h-6 bg-slate-700 mx-2"></div>

                        {/* Comparison Link — hidden behind FEATURES.SHOW_COMPARE */}
                        {FEATURES.SHOW_COMPARE && (
                            <Link to="/compare" onClick={() => window.scrollTo(0, 0)} className={`relative px-3 py-2 rounded-md transition-all duration-200 text-xs font-black uppercase tracking-wider ${isActive('/compare')}`}>
                                Compare
                                {compareList.length > 0 && (
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full animate-bounce-in">
                                        {compareList.length}
                                    </span>
                                )}
                            </Link>
                        )}

                        {/* Saved / Shortlist Link */}
                        <Link to="/shortlist" onClick={() => window.scrollTo(0, 0)} className={`relative p-2 rounded-full transition-all duration-200 ${isActive('/shortlist')}`}>
                            <Heart className={`w-5 h-5 ${savedColleges.length > 0 ? 'fill-pink-500 text-pink-500' : ''}`} />
                            {savedColleges.length > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-pink-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full animate-bounce-in">
                                    {savedColleges.length}
                                </span>
                            )}
                        </Link>

                        {/* Removed Language Switcher from here */}

                        {/* Primary CTA — Start Exploring */}
                        <div className="w-px h-6 bg-slate-700 mx-2"></div>

                        {/* Admin quick link + realtime inbox bell */}
                        {isAdmin && (
                            <>
                                <AdminNotificationBell enabled />
                                <Link
                                    to="/admin"
                                    onClick={() => window.scrollTo(0, 0)}
                                    data-testid="nav-admin-link"
                                    className={`px-3 py-2 rounded-md transition-all duration-200 text-xs font-black uppercase tracking-wider ${isActive('/admin')}`}
                                    title="Admin CMS dashboard"
                                >
                                    Admin
                                </Link>
                            </>
                        )}

                        {/* Admin "View as" Role Switcher — removed per launch-polish request.
                            Simulated-role preview still available via URL param or future toggle. */}

                        <Link
                            to={userData ? '/profile' : '/login'}
                            onClick={() => window.scrollTo(0, 0)}
                            data-testid="nav-primary-cta"
                            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold rounded-xl transition-all shadow-lg shadow-indigo-500/20 active:scale-95"
                        >
                            {userData ? (
                                <>
                                    {userData.avatar ? (
                                        <img src={userData.avatar} alt="" className="w-5 h-5 rounded-full object-cover ring-1 ring-white/30"  loading="lazy" decoding="async"/>
                                    ) : (
                                        <UserIcon className="w-4 h-4" />
                                    )}
                                    {(userData.name || 'Profile').split(' ')[0]}
                                </>
                            ) : (
                                <>
                                    <LogIn className="w-4 h-4" />
                                    Login
                                </>
                            )}
                        </Link>
                    </div>

                    {/* ——————————————————————————————————————————————————————————————————
                       MOBILE TOGGLE (Visible only on Mobile)
                       —————————————————————————————————————————————————————————————————— */}
                    <div className="xl:hidden flex items-center gap-3">
                        {isAdmin && <AdminNotificationBell enabled />}
                        {/* Mobile CTA — sized for thumb-reach (44px target) */}
                        <Link
                            to={userData ? '/profile' : '/login'}
                            data-testid="nav-primary-cta-mobile"
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg shadow-indigo-500/20 active:scale-95 transition-all"
                        >
                            {userData ? (
                                <>
                                    {userData.avatar ? (
                                        <img src={userData.avatar} alt="" className="w-4 h-4 rounded-full object-cover ring-1 ring-white/30" loading="lazy" decoding="async" />
                                    ) : (
                                        <UserIcon className="w-3.5 h-3.5" />
                                    )}
                                    {(userData.name || 'Profile').split(' ')[0]}
                                </>
                            ) : (
                                <>
                                    <LogIn className="w-3.5 h-3.5" />
                                    Login
                                </>
                            )}
                        </Link>

                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            data-testid="nav-mobile-menu-toggle"
                            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                            className="p-2.5 text-slate-300 hover:text-white rounded-lg hover:bg-slate-800 active:bg-slate-700 transition-colors"
                        >
                            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* ——————————————————————————————————————————————————————————————————
               MOBILE MENU DRAWER
               —————————————————————————————————————————————————————————————————— */}
            {isMobileMenuOpen && (
                <div className="xl:hidden absolute top-16 left-0 w-full bg-slate-900 border-b border-slate-800 shadow-2xl animate-fade-in-down">
                        {/* Removed Language Switcher from Mobile Menu */}
                    <div className="px-4 py-6 space-y-2 max-h-[80vh] overflow-y-auto">
                        <MobileLink to="/explore" icon={Compass}>{isPhase1 ? 'Design Courses' : 'Courses'}</MobileLink>
                        <MobileLink to="/library" icon={Play}>Career Insights</MobileLink>
                        <MobileLink to="/colleges" icon={Building2}>{isPhase1 ? 'Design Colleges' : 'Best Colleges'}</MobileLink>
                        {/* Study Abroad hidden for Phase 1 */}
                        <div className="h-px bg-slate-800 my-2"></div>
                        <MobileLink to="/benefits" icon={Zap}>Benefits</MobileLink>
                        <MobileLink to="/eagerminds" icon={Users}>Eager Minds</MobileLink>
                        <MobileLink to="/shortlist" icon={Heart}>Shortlist ({savedColleges.length})</MobileLink>
                        {FEATURES.SHOW_COMPARE && <MobileLink to="/compare" icon={Scale}>Compare ({compareList.length})</MobileLink>}
                        <MobileLink to="/counseling" icon={BookOpen}>{isPhase1 ? 'Design Counseling' : 'Counseling'}</MobileLink>
                        {isAdmin && <MobileLink to="/admin" icon={Network}>Admin</MobileLink>}
                    </div>
                </div>
            )}
        </nav>
    );
}
